<template>
    <v-dialog
        v-model="dialog"
        max-width="900"
        scrollable
    >
        <v-card>
            <v-toolbar dark color="primary">
                <v-icon class="mr-2">mdi-file-document-multiple-outline</v-icon>
                <v-toolbar-title>Meus Termos</v-toolbar-title>
                <v-spacer />
                <v-badge
                    v-if="pendingTerms.length > 0"
                    :content="pendingTerms.length"
                    color="warning"
                    inline
                    class="mr-2"
                >
                    <span class="white--text text-caption">Pendentes</span>
                </v-badge>
                <v-btn icon @click="dialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pa-0">
                <!-- Loading -->
                <div v-if="loading" class="pa-8 text-center">
                    <v-progress-circular indeterminate color="primary" size="48" />
                    <p class="mt-4 text-body-1 grey--text">Carregando seus termos...</p>
                </div>

                <!-- Conteúdo -->
                <div v-else>
                    <v-tabs v-model="tab" class="ma-0" grow>
                        <v-tab>
                            <v-icon left small>mdi-clock-alert-outline</v-icon>
                            Pendentes
                            <v-badge
                                v-if="pendingTerms.length > 0"
                                :content="pendingTerms.length"
                                color="warning"
                                inline
                                class="ml-2"
                            />
                        </v-tab>
                        <v-tab>
                            <v-icon left small>mdi-check-circle-outline</v-icon>
                            Assinados
                            <v-badge
                                v-if="signedTerms.length > 0"
                                :content="signedTerms.length"
                                color="success"
                                inline
                                class="ml-2"
                            />
                        </v-tab>
                    </v-tabs>

                    <v-divider />

                    <v-tabs-items v-model="tab">
                        <!-- Aba Pendentes -->
                        <v-tab-item key="pending">
                            <div v-if="sortedPendingTerms.length === 0" class="pa-8 text-center">
                                <v-icon size="64" color="success">mdi-check-circle</v-icon>
                                <p class="mt-4 text-h6 grey--text">Nenhum termo pendente!</p>
                                <p class="text-body-2 grey--text text--darken-1">
                                    Você está em dia com todos os seus documentos.
                                </p>
                            </div>
                            <v-list v-else two-line>
                                <template v-for="(item, index) in sortedPendingTerms">
                                    <v-list-item :key="item.id">
                                        <v-list-item-avatar>
                                            <v-icon
                                                :color="item.term.is_required ? 'error' : 'grey'"
                                            >
                                                {{ item.term.is_required ? 'mdi-alert-circle' : 'mdi-file-document-outline' }}
                                            </v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title class="d-flex align-center">
                                                {{ item.term.name }}
                                                <v-chip
                                                    v-if="item.term.is_required"
                                                    x-small
                                                    color="error"
                                                    dark
                                                    class="ml-2"
                                                >
                                                    Obrigatório
                                                </v-chip>
                                                <v-chip
                                                    v-else
                                                    x-small
                                                    color="grey"
                                                    outlined
                                                    class="ml-2"
                                                >
                                                    Opcional
                                                </v-chip>
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                                Enviado em: {{ item.created_at | date }}
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                        <v-list-item-action>
                                            <v-btn
                                                color="primary"
                                                small
                                                @click="openSignForm(item)"
                                            >
                                                <v-icon left small>mdi-file-document-edit</v-icon>
                                                Assinar Agora
                                            </v-btn>
                                        </v-list-item-action>
                                    </v-list-item>
                                    <v-divider v-if="index < sortedPendingTerms.length - 1" :key="'divider-' + item.id" />
                                </template>
                            </v-list>
                        </v-tab-item>

                        <!-- Aba Assinados -->
                        <v-tab-item key="signed">
                            <div v-if="signedTerms.length === 0" class="pa-8 text-center">
                                <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
                                <p class="mt-4 text-h6 grey--text">Nenhum termo assinado ainda</p>
                                <p class="text-body-2 grey--text text--darken-1">
                                    Quando você assinar termos, eles aparecerão aqui.
                                </p>
                            </div>
                            <v-list v-else two-line>
                                <template v-for="(item, index) in signedTerms">
                                    <v-list-item :key="item.id">
                                        <v-list-item-avatar>
                                            <v-icon color="success">mdi-check-circle</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ item.term.name }}
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                <v-icon x-small class="mr-1">mdi-calendar-check</v-icon>
                                                Assinado em: {{ item.updated_at | dateTime }}
                                                <span v-if="item.term.is_required && item.agree" class="ml-2">
                                                    <v-icon x-small color="success">mdi-check</v-icon>
                                                    {{ item.term.label }}
                                                </span>
                                                <span v-else-if="item.option" class="ml-2">
                                                    <v-icon x-small color="primary">mdi-check</v-icon>
                                                    {{ item.option.label }}
                                                </span>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                        <v-list-item-action class="flex-row">
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn
                                                        icon
                                                        color="purple"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        @click="openViewForm(item)"
                                                    >
                                                        <v-icon>mdi-text-box-search-outline</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Visualizar Termo</span>
                                            </v-tooltip>
                                            <v-tooltip bottom v-if="item.url_document || item.url_signed_document">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn
                                                        icon
                                                        color="primary"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        @click="downloadPdf(item)"
                                                    >
                                                        <v-icon>mdi-download</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Baixar PDF</span>
                                            </v-tooltip>
                                        </v-list-item-action>
                                    </v-list-item>
                                    <v-divider v-if="index < signedTerms.length - 1" :key="'divider-signed-' + item.id" />
                                </template>
                            </v-list>
                        </v-tab-item>
                    </v-tabs-items>
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn text @click="dialog = false">
                    Fechar
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Modal de assinatura/visualização -->
        <sys-register-term-signature-form
            :show.sync="showSignForm"
            :signature-id="selectedItem.id"
            :token="token"
            :view="isViewMode"
            :use-teacher-auth="!token"
            @onCompleted="onSignCompleted"
        />

        <!-- Alert para mensagens -->
        <emc-alert-snackbar :show="!!message.length" :message="message" :color="error ? 'error' : 'success'" />
    </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'

export default {
    name: 'SysTeacherTermsHub',

    data: () => ({
        dialog: false,
        loading: false,
        tab: 0,
        items: [],
        selectedItem: { id: 0 },
        showSignForm: false,
        isViewMode: false,
        error: false,
        message: ''
    }),

    props: {
        show: {
            type: Boolean,
            default: false
        },
        teacherId: {
            type: Number,
            required: true
        },
        token: {
            type: String,
            default: null
        }
    },

    computed: {
        pendingTerms() {
            return this.items.filter(item => item.term_signature_status_id === 1 && item.term && !item.term.deleting)
        },

        signedTerms() {
            return this.items.filter(item => item.term_signature_status_id === 2 && item.term && !item.term.deleting)
        },

        sortedPendingTerms() {
            return [...this.pendingTerms].sort((a, b) => {
                // Obrigatórios primeiro
                if (a.term.is_required && !b.term.is_required) return -1
                if (!a.term.is_required && b.term.is_required) return 1
                // Depois por data (mais antigos primeiro)
                return new Date(a.created_at) - new Date(b.created_at)
            })
        }
    },

    watch: {
        show(val) {
            this.dialog = val
            if (val) {
                this.loadTerms()
            }
        },

        dialog(val) {
            this.$emit('update:show', val)
        }
    },

    methods: {
        ...mapActions('teacher', ['ActionFindTeacher', 'ActionFindTeacherByToken']),

        async loadTerms() {
            if (!this.teacherId) return

            this.loading = true
            this.items = []
            this.error = false
            this.message = ''

            try {
                const method = this.token ? 'ActionFindTeacherByToken' : 'ActionFindTeacher'

                const payload = {
                    with: 'termSignatures.term,termSignatures.status,termSignatures.option'
                }

                if (this.token) {
                    payload.token = this.token
                } else {
                    payload.id = this.teacherId
                }

                const response = await this[method](payload)
                this.items = response.data.termSignatures || []
            } catch (err) {
                console.error('Erro ao carregar termos:', err)
                this.error = true
                this.message = 'Erro ao carregar seus termos. Tente novamente.'
            } finally {
                this.loading = false
            }
        },

        openSignForm(item) {
            this.selectedItem = item
            this.isViewMode = false
            this.showSignForm = true
        },

        openViewForm(item) {
            this.selectedItem = item
            this.isViewMode = true
            this.showSignForm = true
        },

        onSignCompleted() {
            this.showSignForm = false
            this.message = 'Termo assinado com sucesso!'
            this.loadTerms()
        },

        downloadPdf(item) {
            const apiRoot = process.env.VUE_APP_ROOT_API
            const token = this.$store.state.auth.token
            const downloadUrl = `${apiRoot}teachers/term-signatures/${item.id}/download?token=${token}`
            window.open(downloadUrl, '_blank')
        }
    }
}
</script>

<style scoped>
.v-list-item__action.flex-row {
    flex-direction: row;
}
</style>
